import React from 'react';
import { X, Heart, Trash2, ArrowRight, Star, MapPin } from 'lucide-react';
import { Companion } from '../types';

interface WishlistDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  wishlistIds: string[];
  allCompanions: Companion[];
  onRemove: (id: string, e: React.MouseEvent) => void;
  onSelectCompanion: (companion: Companion) => void;
}

export const WishlistDrawer: React.FC<WishlistDrawerProps> = ({
  isOpen,
  onClose,
  wishlistIds,
  allCompanions,
  onRemove,
  onSelectCompanion,
}) => {
  if (!isOpen) return null;

  const wishlistedCompanions = allCompanions.filter((c) => wishlistIds.includes(c.id));

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-slate-950/60 backdrop-blur-xs flex justify-end animate-in fade-in duration-200">
      <div className="w-full max-w-md bg-white h-full shadow-2xl flex flex-col justify-between animate-in slide-in-from-right duration-300">
        
        {/* Drawer Header */}
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center">
              <Heart className="w-4 h-4 fill-pink-500 text-pink-500" />
            </div>
            <div>
              <h3 className="font-extrabold text-slate-900 text-base">Saved Companions</h3>
              <p className="text-xs text-slate-400">{wishlistedCompanions.length} Profiles Saved</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-100 text-slate-500 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Wishlist Items List */}
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {wishlistedCompanions.length > 0 ? (
            wishlistedCompanions.map((comp) => (
              <div
                key={comp.id}
                className="flex items-center gap-3 p-3 rounded-2xl border border-slate-100 hover:border-pink-200 hover:bg-pink-50/30 transition-all group"
              >
                <img
                  src={comp.image}
                  alt={comp.name}
                  className="w-16 h-16 rounded-xl object-cover"
                  referrerPolicy="no-referrer"
                />
                
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-slate-900 text-sm truncate">
                    {comp.name}, {comp.age}
                  </h4>
                  <div className="flex items-center gap-2 text-xs text-slate-500 my-0.5">
                    <span className="flex items-center gap-0.5">
                      <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {comp.rating}
                    </span>
                    <span>•</span>
                    <span className="truncate">{comp.city}</span>
                  </div>
                  <p className="text-xs font-bold text-pink-600">₹{comp.hourlyRate}/hr</p>
                </div>

                <div className="flex flex-col gap-1 items-end">
                  <button
                    onClick={(e) => onRemove(comp.id, e)}
                    className="p-1.5 text-slate-400 hover:text-red-500 transition-colors"
                    title="Remove from saved"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => {
                      onSelectCompanion(comp);
                      onClose();
                    }}
                    className="px-2.5 py-1 bg-pink-500 hover:bg-pink-600 text-white text-[11px] font-bold rounded-lg transition-colors"
                  >
                    Book
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-16 space-y-3">
              <div className="w-12 h-12 rounded-full bg-pink-50 text-pink-400 flex items-center justify-center mx-auto text-xl">
                ♥
              </div>
              <p className="font-bold text-slate-700 text-sm">No Saved Companions Yet</p>
              <p className="text-xs text-slate-400 max-w-[200px] mx-auto">
                Click the heart icon on any companion card to add them to your favorites for quick booking.
              </p>
            </div>
          )}
        </div>

        {/* Drawer Bottom */}
        <div className="p-5 border-t border-slate-100 bg-slate-50/50">
          <button
            onClick={onClose}
            className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl transition-colors"
          >
            Continue Browsing
          </button>
        </div>

      </div>
    </div>
  );
};
